import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {StyleSheet, View, Image} from 'react-native';
import {useAuthAccess, useToast} from 'hooks';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams} from 'navigation/Routes';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Table, TagsInput, Icon, Button, MultiSelect, Text} from 'ui-kits';
import {
  selectedProjectTeam,
  selectedProjectID,
  projectSuccess,
  projectFailure,
} from 'redux/selectors/projectSelector';
import {
  projectTeamMember,
  addEmployeeToProjectRequest,
  resetMessage,
  getProjectTeamMemberRequest,
} from 'redux/slices/projectSlice';
import {defaultUserProfile} from 'constants/DefaultValues';
import {isEqual} from 'lodash';
import {
  getEmployeesRequest,
  getEmployeesRequestParams,
  Employee,
} from 'redux/slices/employeeSlice';
import {getOrgID} from 'redux/selectors/authSelector';
import {
  employeeNextPage,
  allEmployeesList,
} from 'redux/selectors/employeeSelector';
import {useDispatch, useSelector} from 'react-redux';
import {userDesignation} from 'constants/enums';
import {SearchDropDown} from 'ui-kits/Input/MultiSelect/SearchDropDown';
import {UserPermissions} from 'redux/slices/projectAnalyticsSlice';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';

type Column<T> = {
  key: keyof T;
  label: JSX.Element | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  renderCell?: (cellData: T) => JSX.Element | null;
};

const allPermissions: UserPermissions[] = Object.entries(userDesignation).map(
  ([name, id]) => ({
    id,
    name,
  }),
);

export function ProjectTeamManagement({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const {hasWriteAccess} = useAuthAccess();
  const projectTeamList = useSelector(selectedProjectTeam);
  const dispatch = useDispatch();
  const canEdit = hasWriteAccess;
  const [projectMembers, setProjectMembers] =
    useState<projectTeamMember[]>(projectTeamList);
  const projectID = useSelector(selectedProjectID);
  const {showToasts} = useToast();

  // a state vairable to force component re-render for AllEmployee Dropdown
  const [dropdownKey, setDropdownKey] = useState<number>(0);

  const addMemberToProjectSuccess = useSelector(projectSuccess);
  const addMemberToProjectError = useSelector(projectFailure);

  const isSame = useMemo(
    () => isEqual(projectMembers, projectTeamList),
    [projectMembers, projectTeamList],
  );

  // fetch team members function
  const fetchTeamMember = useCallback(() => {
    dispatch(
      getProjectTeamMemberRequest({
        proj_id: projectID,
      }),
    );
  }, [projectID, dispatch]);

  // show toast message
  useEffect(() => {
    if (addMemberToProjectSuccess) {
      if (addMemberToProjectSuccess === 'S-10005') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(addMemberToProjectSuccess as SuccessCodes),
          'success',
        );
        fetchTeamMember();
      }
      dispatch(resetMessage());
    }
    if (addMemberToProjectError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(addMemberToProjectError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [
    addMemberToProjectSuccess,
    addMemberToProjectError,
    dispatch,
    showToasts,
    fetchTeamMember,
  ]);

  // set local state
  useEffect(() => {
    setProjectMembers(projectTeamList);
  }, [projectTeamList]);

  const [currentEmployeeData, setCurrentEmployeeData] =
    useState<projectTeamMember | null>(null);

  const columns: Column<projectTeamMember>[] = [
    {
      key: 'first_name',
      label: 'Project Member',
      minWidth: 300,
      renderCell: item => (
        <View style={styles.memberWrapper}>
          <Image
            style={styles.profile}
            source={{uri: item.profile_photo_url ?? defaultUserProfile}}
          />
          <Text style={styles.memberName}>
            {item?.first_name?.concat(' ', item?.last_name ?? '')}
          </Text>
        </View>
      ),
    },
    {
      maxWidth: 600,
      key: 'permissions',
      label: 'Permissions',
      renderCell: item => {
        return (
          <View style={styles.permissionWraper}>
            <TagsInput
              onChange={permissions =>
                editPermissions({
                  id: item.id,
                  permissions: permissions.map(p => ({
                    name: p,
                    id: userDesignation[p],
                  })),
                })
              }
              editable={canEdit}
              values={item.permissions.map(item1 => {
                const matchingPermission = allPermissions.filter(
                  p => p.id === item1.id,
                )[0];
                return matchingPermission.name;
              })}
              inputMode={false}
              onDropDownOpen={() => setCurrentEmployeeData(item)}
            />
          </View>
        );
      },
    },
  ];

  const deleteColumn: Column<projectTeamMember> = {
    key: 'id',
    label: '',
    renderCell: item => (
      <Icon type="trash" onPress={() => removeTeamMember(item)} />
    ),
    maxWidth: 60,
    minWidth: 50,
  };

  function removeTeamMember(member: projectTeamMember) {
    setProjectMembers(p => p.filter(item => item.id !== member.id));
  }

  function addMember(members: projectTeamMember[]) {
    setProjectMembers(prevMembers => {
      const existingIds = new Set(prevMembers.map(member => member.id));
      const newMembers: projectTeamMember[] = [];

      for (const emp of members) {
        if (!existingIds.has(emp.id)) {
          newMembers.push({
            emp_id: emp.emp_id,
            id: emp.id,
            first_name: emp.first_name,
            last_name: emp.last_name,
            role: emp.role,
            profile_photo_url: emp.profile_photo_url,
            permissions: [],
          });
        }
      }

      return [...prevMembers, ...newMembers];
    });
  }

  function editPermissions(member: {
    id: number;
    permissions: UserPermissions[];
  }) {
    setProjectMembers(p => {
      const updated_state = structuredClone(p);
      for (let i of updated_state) {
        if (i.id === member.id) {
          i.permissions = member.permissions;
        }
      }
      return updated_state;
    });
  }

  function handleCancel() {
    // reset the data
    setProjectMembers(projectTeamList);
    setDropdownKey(p => p + 1);
  }

  function onSave() {
    const existing_employess = projectMembers.map(member => {
      return {
        id: member.emp_id,
        permissions: member.permissions.map(item => item.id),
      };
    });

    dispatch(
      addEmployeeToProjectRequest({
        proj_id: projectID,
        employees: existing_employess,
      }),
    );
    setDropdownKey(p => p + 1);
  }

  return (
    <View style={styles.main}>
      <Text style={styles.mainHeader}>
        {canEdit ? 'Access Manager' : 'Project Members'}
      </Text>

      {canEdit ? (
        <AllEmployeeDropDown
          existingID={projectTeamList.map(item => item.id)}
          onSelect={addMember}
          dropdownKey={dropdownKey}
        />
      ) : null}

      <Table
        keyExtractor={(item: projectTeamMember) => item?.id?.toString()}
        data={projectMembers}
        columns={canEdit ? [...columns, deleteColumn] : columns}
        pagination={false}
      />
      {canEdit ? (
        <View style={styles.submitButtonWrapper}>
          <Button
            style={styles.button}
            onPress={handleCancel}
            variant="secondary"
            text="Cancel"
            disabled={isSame}
          />
          <Button style={styles.button} onPress={onSave} text="Save" />
        </View>
      ) : null}

      {currentEmployeeData ? (
        <SearchDropDown
          selectedEntries={currentEmployeeData.permissions}
          searchKeyExtractor={(item: UserPermissions) => item.name}
          searchPlaceholder={'Search'}
          onClose={() => setCurrentEmployeeData(null)}
          onSave={entries => {
            setCurrentEmployeeData(null);
            editPermissions({
              id: currentEmployeeData.id,
              permissions: entries,
            });
          }}
          options={allPermissions}
          optionKeyExtractor={(item: UserPermissions) => item.id.toString()}
          renderOption={(item: UserPermissions) => <Text>{item.name}</Text>}
          style={styles.permissionDropdown}
          header={'permissions'}
        />
      ) : null}
    </View>
  );
}

type AllEmployeeDropDownProp = Readonly<{
  onSelect: (item: projectTeamMember[]) => void;
  dropdownKey: any;
  existingID: number[];
}>;
function AllEmployeeDropDown(props: AllEmployeeDropDownProp) {
  const {onSelect, dropdownKey, existingID = []} = props;
  const org_id = useSelector(getOrgID);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedEntries, setSelectedEntries] = useState<Employee[]>([]);
  const allEmployees = useSelector(allEmployeesList);
  const nextEmployeePage = useSelector(employeeNextPage);
  const fetchEmployee = useCallback(() => {
    const payload: getEmployeesRequestParams = {
      org_id: org_id as any,
      page: currentPage,
    };

    dispatch(getEmployeesRequest(payload));
  }, [dispatch, org_id, currentPage]);

  // filter inactive employee and also employee who are already
  // existing as a team member for current project
  const allActiveEmployee = useMemo(
    () =>
      allEmployees.filter(
        item => item.is_active && !existingID.includes(item.id),
      ),
    [allEmployees, existingID],
  );

  useEffect(() => {
    // if employee list is empty then fetch them
    fetchEmployee();
  }, [fetchEmployee]);

  function fetchNextPage() {
    if (nextEmployeePage) {
      setCurrentPage(nextEmployeePage);
    }
  }

  useEffect(() => {
    if (dropdownKey) {
      setSelectedEntries([]);
    }
  }, [dropdownKey]);

  function renderItem(item: Employee) {
    return (
      <View style={styles.memberWrapper}>
        <Image
          source={{uri: item.profile_photo_url ?? defaultUserProfile}}
          style={styles.profile}
        />
        <View style={styles.memberInfo}>
          <Text style={styles.memberName}>
            {item.first_name?.concat(item.last_name ?? '')}
          </Text>
          <Text style={styles.memberRole}>{item.email}</Text>
        </View>

        <View style={styles.engagedWrapper}>
          <Text style={styles.engagedLabel}>Engaged</Text>
          <Text style={styles.engagedNumber}>{item.project?.length}</Text>
        </View>
      </View>
    );
  }

  return (
    <MultiSelect
      options={allActiveEmployee}
      searchKeyExtractor={(item: Employee) =>
        item.first_name?.concat(item?.last_name ?? '') ?? ''
      }
      keyExtractor={(item: Employee) => item?.id?.toString()}
      renderItem={renderItem}
      dropDownStyle={styles.employeeDropDownStyle}
      style={styles.employeeSelectWrapper}
      fetchNextHandler={fetchNextPage}
      selectedEntries={selectedEntries}
      onSave={entries => {
        setSelectedEntries(entries);
        onSelect?.(entries);
      }}
    />
  );
}

const styles = StyleSheet.create({
  permissionDropdown: {
    top: 200,
    right: '50%',
    transform: [{translateX: '50%'}],
  },
  memberInfo: {},
  engagedLabel: {
    backgroundColor: colors.palette.brandBlue,
    color: colors.palette.white,
    fontWeight: 500,
    paddingVertical: 2,
    paddingHorizontal: spacing['1'],
    paddingRight: spacing['3'],
    fontSize: 12,
    borderRadius: corner_radius['rounded-md'],
  },
  engagedNumber: {
    backgroundColor: colors.palette.brandPink,
    color: colors.palette.white,
    borderRadius: 999,
    fontSize: 10,
    borderWidth: 2,
    borderColor: colors.palette.white,
    paddingHorizontal: spacing['1'],
    position: 'relative',
    transform: [{translateX: -9}],
  },
  engagedWrapper: {
    marginLeft: 'auto',
    marginRight: spacing['1'],
    flexDirection: 'row',
    position: 'relative',
    alignItems: 'center',
  },
  memberSelectWrapper: {},
  employeeSelectWrapper: {
    width: 400,
    alignSelf: 'center',
  },
  main: {
    padding: spacing['6'],
    flex: 1,
    // backgroundColor: 'red',
  },
  mainHeader: {
    fontWeight: 600,
    fontSize: 40,
    color: colors.palette.black,
  },
  tableContainer: {
    flex: 1,
    backgroundColor: 'red',
  },
  profile: {
    width: 32,
    height: 32,
    borderRadius: 999,
  },
  memberWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['4'],
  },
  permissionWraper: {
    paddingHorizontal: spacing['2'],
  },
  submitButtonWrapper: {
    flexDirection: 'row',
    gap: spacing['3'],
    justifyContent: 'center',
  },
  button: {
    width: 130,
  },
  employeeDropDownStyle: {
    top: '18%',
    right: '50%',
    transform: [{translateX: '50%'}],
    minHeight: 350,
  },
  memberName: {
    fontSize: 16,
    fontWeight: 500,
    color: colors.palette.secondaryBlack5,
  },
  memberRole: {
    fontSize: 12,
    fontWeight: 500,
    color: colors.palette.secondaryBlack5,
  },
});
